import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { AppHeader } from "@/components/AppHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Download, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { deleteAccount } from "@/lib/account.functions";

export const Route = createFileRoute("/settings")({
  head: () => ({
    meta: [
      { title: "Settings — RoleBoard" },
      { name: "description", content: "Manage your RoleBoard account, export your data, or delete your account." },
      { property: "og:title", content: "Settings — RoleBoard" },
      { property: "og:description", content: "Manage your RoleBoard account and your data." },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://roleboard.app/settings" }],
  }),
  component: SettingsPage,
});

function SettingsPage() {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<{ first_name: string | null; last_name: string | null; digest_unsubscribed: boolean } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [digestSaving, setDigestSaving] = useState(false);
  const [digestSaved, setDigestSaved] = useState(false);
  const [digestError, setDigestError] = useState(false);
  const deleteFn = useServerFn(deleteAccount);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("first_name,last_name,digest_unsubscribed")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => setProfile(data ?? null));
  }, [user]);

  async function handleExport() {
    if (!user) return;
    setExporting(true);
    try {
      const [auditionsRes, sessionsRes, profileRes, tokensRes] = await Promise.all([
        supabase.from("auditions").select("*").eq("user_id", user.id),
        supabase.from("sessions").select("*").eq("user_id", user.id),
        supabase.from("profiles").select("*").eq("id", user.id).maybeSingle(),
        supabase.from("calendar_feed_tokens").select("token,created_at").eq("user_id", user.id),
      ]);
      const payload = {
        exported_at: new Date().toISOString(),
        account: { id: user.id, email: user.email },
        profile: profileRes.data,
        auditions: auditionsRes.data ?? [],
        sessions: sessionsRes.data ?? [],
        calendar_tokens: tokensRes.data ?? [],
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `roleboard-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data has been exported.");
    } catch (e: any) {
      toast.error(e?.message ?? "Export failed");
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete() {
    if (confirmText !== "DELETE") return;
    setDeleting(true);
    try {
      await deleteFn({});
      await signOut();
      toast.success("Your account has been deleted.");
      navigate({ to: "/" });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not delete account");
      setDeleting(false);
    }
  }

  if (loading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <AppHeader />
      <main className="flex-1">
        <div className="container max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-14">
          <header className="mb-10">
            <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Manage your account and your data.
            </p>
          </header>

          {/* Profile */}
          <ProfileSection
            userId={user.id}
            initialFirst={profile?.first_name ?? ""}
            initialLast={profile?.last_name ?? ""}
            onSaved={(fn, ln) => setProfile((prev) => ({ ...(prev ?? { digest_unsubscribed: false }), first_name: fn, last_name: ln }))}
          />

          {/* Password */}
          <PasswordSection email={user.email ?? ""} />

          {/* Email Preferences */}
          <section className="rounded-2xl border border-border bg-card/40 p-6 mb-6">
            <h2 className="text-lg font-medium tracking-tight">Email Preferences</h2>
            <div className="mt-5 space-y-5">
              {/* Weekly digest */}
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">Weekly Callsheet</span>
                    {digestSaved && (
                      <span className="inline-flex items-center gap-1 text-xs text-primary animate-in fade-in slide-in-from-left-1 duration-200">
                        <Check className="size-3" />
                        Saved
                      </span>
                    )}
                    {digestError && (
                      <span className="text-xs text-destructive animate-in fade-in slide-in-from-left-1 duration-200">
                        Could not save
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    A weekly summary of your auditions, callbacks, and career momentum — every Monday morning.
                  </p>
                </div>
                <Switch
                  checked={!(profile?.digest_unsubscribed ?? false)}
                  disabled={!profile || digestSaving}
                  onCheckedChange={async (checked) => {
                    if (!user) return;
                    setDigestSaving(true);
                    setDigestSaved(false);
                    setDigestError(false);
                    try {
                      const { error } = await supabase
                        .from("profiles")
                        .update({ digest_unsubscribed: !checked })
                        .eq("id", user.id);
                      if (error) throw error;
                      setProfile((prev) => prev ? { ...prev, digest_unsubscribed: !checked } : null);
                      setDigestSaved(true);
                      setTimeout(() => setDigestSaved(false), 2000);
                    } catch {
                      setDigestError(true);
                      setTimeout(() => setDigestError(false), 3000);
                    } finally {
                      setDigestSaving(false);
                    }
                  }}
                  className={digestError ? "data-[state=unchecked]:bg-destructive data-[state=checked]:bg-destructive" : undefined}
                />
              </div>

              {/* Product updates */}
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">Product updates</span>
                    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                      Coming soon
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Occasional emails about new features and improvements to RoleBoard.
                  </p>
                </div>
                <Switch disabled checked={false} />
              </div>
            </div>
          </section>

          {/* Your Data */}
          <section className="rounded-2xl border border-border bg-card/40 p-6 mb-6">
            <h2 className="text-lg font-medium tracking-tight">Your data</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Download a copy of your auditions, sessions, profile, and calendar
              sync data as a JSON file. Your career data belongs to you.
            </p>
            <Button
              onClick={handleExport}
              disabled={exporting}
              variant="outline"
              className="mt-4"
            >
              {exporting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Download className="size-4" />
              )}
              Export my data
            </Button>
          </section>

          {/* Legal */}
          <section className="rounded-2xl border border-border bg-card/40 p-6 mb-6">
            <h2 className="text-lg font-medium tracking-tight">Legal</h2>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm">
              <Link to="/privacy" className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline">
                Terms of Service
              </Link>
            </div>
          </section>

          {/* Danger */}
          <section className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6">
            <h2 className="text-lg font-medium tracking-tight text-foreground">
              Delete account
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              This permanently removes your account, auditions, uploaded tapes,
              and calendar sync data. This cannot be undone.
            </p>
            <Button
              onClick={() => setDeleteOpen(true)}
              variant="destructive"
              className="mt-4"
            >
              <Trash2 className="size-4" />
              Delete my account
            </Button>
          </section>
        </div>
      </main>
      <SiteFooter />

      <Dialog open={deleteOpen} onOpenChange={(o) => !deleting && setDeleteOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>
              This permanently removes your account, all auditions, uploaded
              tapes, and calendar sync data. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="confirm">Type <span className="font-mono font-semibold">DELETE</span> to confirm</Label>
            <Input
              id="confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting || confirmText !== "DELETE"}
            >
              {deleting ? <Loader2 className="size-4 animate-spin" /> : "Delete account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionShell({
  title,
  value,
  editing,
  onToggle,
  editLabel,
  children,
}: {
  title: string;
  value: React.ReactNode;
  editing: boolean;
  onToggle: () => void;
  editLabel: string;
  children?: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card/40 p-6 mb-6 transition-all">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-sm font-medium tracking-tight text-muted-foreground uppercase">
            {title}
          </h2>
          <div className="mt-2 text-[15px] text-foreground truncate">{value}</div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="text-muted-foreground hover:text-foreground -mr-2"
        >
          {editing ? "Cancel" : editLabel}
        </Button>
      </div>
      {editing && (
        <div className="mt-5 pt-5 border-t border-border/60 animate-in fade-in slide-in-from-top-1 duration-200">
          {children}
        </div>
      )}
    </section>
  );
}

function ProfileSection({
  userId,
  initialFirst,
  initialLast,
  onSaved,
}: {
  userId: string;
  initialFirst: string;
  initialLast: string;
  onSaved: (fn: string, ln: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState(initialFirst);
  const [lastName, setLastName] = useState(initialLast);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFirstName(initialFirst);
    setLastName(initialLast);
  }, [initialFirst, initialLast]);

  const displayName = [initialFirst, initialLast].filter(Boolean).join(" ") || "—";

  async function handleSave() {
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn || !ln) return toast.error("Please enter your first and last name");
    if (fn.length > 80 || ln.length > 80) return toast.error("Names must be under 80 characters");
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .upsert({ id: userId, first_name: fn, last_name: ln }, { onConflict: "id" });
      if (error) throw error;
      onSaved(fn, ln);
      toast.success("Profile updated");
      setEditing(false);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionShell
      title="Profile"
      value={displayName}
      editing={editing}
      onToggle={() => {
        if (editing) {
          setFirstName(initialFirst);
          setLastName(initialLast);
        }
        setEditing(!editing);
      }}
      editLabel="Edit name"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="first-name">First name</Label>
          <Input
            id="first-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={80}
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="last-name">Last name</Label>
          <Input
            id="last-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            maxLength={80}
          />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="size-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </SectionShell>
  );
}

function PasswordSection({ email }: { email: string }) {
  const [editing, setEditing] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  function resetFields() {
    setCurrentPassword("");
    setPassword("");
    setConfirm("");
  }

  async function handleSave() {
    if (!currentPassword) return toast.error("Enter your current password");
    if (password.length < 6) return toast.error("New password must be at least 6 characters");
    if (password.length > 72) return toast.error("New password must be under 72 characters");
    if (password !== confirm) return toast.error("New passwords don't match");
    if (password === currentPassword) return toast.error("New password must be different from your current password");
    if (!email) return toast.error("Please sign in again and retry");
    setSaving(true);
    try {
      // Get a fresh access token for the GoTrue REST call.
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        toast.error("Please sign in again and retry");
        return;
      }

      // Supabase's "Secure password change" setting requires current_password
      // in the PUT /auth/v1/user body. supabase-js updateUser() does not send
      // that field, so we call the REST endpoint directly.
      const url = `${import.meta.env.VITE_SUPABASE_URL}/auth/v1/user`;
      const apiKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          apikey: apiKey,
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ password, current_password: currentPassword }),
      });

      if (!res.ok) {
        let code: string | undefined;
        try {
          const body = await res.json();
          code = body?.error_code ?? body?.code;
        } catch {
          // ignore parse errors — fall through to generic message
        }
        if (
          res.status === 400 &&
          (code === "invalid_credentials" || code === "current_password_mismatch")
        ) {
          toast.error("Current password is incorrect");
        } else if (res.status === 401) {
          toast.error("Please sign in again and retry");
        } else if (res.status === 422) {
          toast.error("New password doesn't meet the requirements");
        } else {
          toast.error("Unable to update password");
        }
        return;
      }

      resetFields();
      toast.success("Password updated");
      setEditing(false);
    } catch {
      toast.error("Unable to update password");
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionShell
      title="Password"
      value={<span className="font-mono tracking-widest">••••••••</span>}
      editing={editing}
      onToggle={() => {
        if (editing) resetFields();
        setEditing(!editing);
      }}
      editLabel="Change password"
    >
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="current-password">Current password</Label>
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            maxLength={72}
            autoFocus
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={72}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              maxLength={72}
            />
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="size-4 animate-spin" /> : "Update password"}
        </Button>
      </div>
    </SectionShell>
  );
}
